import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $patchStyleText } from "@lexical/selection";
import { IconButton, Tooltip } from "@mui/material";
import { $getSelection, $isRangeSelection } from "lexical";
import type { FC } from "react";
import { useCallback, useRef } from "react";
import { FormatTextColour } from "../../../../../styles/icons/format-text-colour";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../generic/components/colour-picker/colour-picker";
import { floatingToolbarStyles } from "./floating-toolbar-styles";
import type { ToolbarProps } from "./props";

export const FontColourSelect: FC<ToolbarProps & { colour: string }> = ({
  disabled,
  colour,
  mode,
}) => {
  const [editor] = useLexicalComposerContext();
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  const { handleToggle, anchorRef } = popover;

  const handleChange = useCallback(
    (newColour: string, skipHistory?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) $patchStyleText(selection, { color: newColour });
        },
        skipHistory ? { tag: "historic" } : {},
      );
    },
    [editor],
  );

  return (
    <>
      <Tooltip title="Font Color">
        <span>
          <IconButton
            data-analytics-id="lexical-toolbar-font-color"
            disabled={disabled}
            data-testid="toolbar-button-font-color"
            aria-label="Font Color"
            tabIndex={-1}
            sx={{
              ...floatingToolbarStyles[mode].button,
              backgroundColor: "background.neutral",
            }}
            onClick={handleToggle}
            ref={anchorRef}
          >
            <FormatTextColour textColour={colour} sx={floatingToolbarStyles[mode].formatIcon} />
          </IconButton>
        </span>
      </Tooltip>
      <ColourPicker
        popover={popover}
        colour={colour}
        onChange={handleChange}
        inputRef={popoverInputRef}
      />
    </>
  );
};
